import {Component, DestroyRef, DoCheck, OnDestroy, OnInit} from '@angular/core';
import {NgClass} from '@angular/common';
import {LookupService} from '../../common/service/lookup.service';
import {NgEventBus} from 'ng-event-bus';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PDF_CREATION_METHOD, PLAN_ACTION} from '../../common/constants';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {FormerTenureCardinality} from '../../common/models/former-tenure-cardinality.model';
import {ParentPlanType} from '../../common/models/parent-plan-type.model';
import {SurveyCategoryLookup} from '../../common/models/survey-category.model';
import {PlanTypeLookup} from '../../common/models/plan-type.model';
import {FormerTenureSubjectOfPlan} from '../../common/models/former-tenure-subject-of-plan.model';
import {PlanPurposeLookup} from '../../common/models/plan-purpose.model';
import {PlanHeadingFormat} from '../../common/models/plan-heading-fornat.model';
import {isDefined} from '../../common/service/data-util';
import {TraitsService} from '../../common/service/traits.service';
import {HttpClient} from '@angular/common/http';
import {CustomLot} from '../../common/models/custom-lot.model';
import {ParentLot} from '../../common/models/parent-lot.model';
import {AlertModalService} from '../../common/component/modal';
import {FormerTenureValidity} from '../../common/models/former-tenure-validity.model';
import {AppConfigService} from '../../common/service/app-config.service';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {Subscription} from 'rxjs';
import {TenureService} from '../tenure/tenure.service';
import {MaxLengthInputLimitDirective} from '../../common/directive/max-length-input-limit.directive';
import {NgSelectModule} from '@ng-select/ng-select';
import {PlanHeadingService} from '../../common/service/plan-heading.service';
import {NlrFocusDirective} from '../../common/directive/nlr-focus.directive';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-plan-details',
    imports: [
        FormsModule,
        NgClass,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        MaxLengthInputLimitDirective,
        NgSelectModule,
        NlrFocusDirective
    ],
    templateUrl: './plan-details.component.html',
    styleUrl: './plan-details.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class PlanDetailsComponent implements OnInit, OnDestroy, DoCheck {
    tenureTypes: SurveyCategoryLookup[] = [];
    planPurposeList: PlanPurposeLookup[] = [];
    planTypeList: PlanTypeLookup[] = [];
    formerTenureValidityLookups = [];
    parentPlanTypeLookups: ParentPlanType[] = [];
    formerTenureSubjects: FormerTenureSubjectOfPlan[] = [];
    planHeadingFormatLookup: PlanHeadingFormat[] = [];
    planPurposes: PlanPurposeLookup[] = [];
    planTypes: PlanTypeLookup[] = [];
    action: string = PLAN_ACTION.NEW;
    get readonly() {
        return this.planModelService.readonly;
    }
    title: string = 'PlanDetailsComponent';
    private loadStrataDetails: number;
    private disableAddBtn: boolean;
    private selectedFormerTenure: FormerTenureCardinality[] = undefined;
    private applicableSubjectOfPlans = [];
    private selectedFormerTenureValidity = [];
    private formerTenureCardinalityLookups: FormerTenureCardinality[];
    private parentLots: number[];
    private modelUpdatesSubs: Subscription;

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private traitsService: TraitsService,
        private alertModalService: AlertModalService,
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private tenureService: TenureService,
        private planHeadingService: PlanHeadingService,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.initialiseLookups();
        this.modelUpdatesSubs = this.eventBus
            .on('nlr:plan-model-updated:model')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.filterPlanTypes();
                this.filterPurposes();
            });
        // The plan purpose list is scoped to the plan's PDF creation method, so re-filter it whenever that method changes
        // (e.g. via the hamburger "Create PDF from CAD / in Portal / Digital Plan" actions).
        this.modelUpdatesSubs.add(
            this.eventBus
                .on('nlr:plan-model-updated:pdfCreationMethod')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.filterPurposes())
        );
    }

    ngOnDestroy() {
        this.modelUpdatesSubs.unsubscribe();
    }
    private initialiseLookups() {
        this.parentPlanTypeLookups = this.lookupService.getLookups<ParentPlanType>('parentPlanType');
        this.formerTenureSubjects = this.lookupService.getLookups<FormerTenureSubjectOfPlan>('formerTenureSubjectOfPlan');
        this.formerTenureCardinalityLookups = this.lookupService.getLookups<FormerTenureCardinality>('formerTenureCardinality');
        this.formerTenureValidityLookups = this.lookupService.getLookups<FormerTenureValidity>('formerTenureValidity');
        this.tenureTypes = this.lookupService
            .getLookups<SurveyCategoryLookup>('tenureType')
            .sort((a: SurveyCategoryLookup, b: SurveyCategoryLookup) => a.uiOptionOrder - b.uiOptionOrder);
        this.planTypeList = this.lookupService
            .getLookups<PlanTypeLookup>('planType')
            .sort((a: PlanTypeLookup, b: PlanTypeLookup) => a.uiOptionOrder - b.uiOptionOrder);
        this.planPurposeList = this.lookupService
            .getLookups<PlanPurposeLookup>('planPurpose')
            .sort((a: PlanPurposeLookup, b: PlanPurposeLookup) => a.uiOptionOrder - b.uiOptionOrder);
        this.planHeadingFormatLookup = this.lookupService.getLookups<PlanHeadingFormat>('planHeadingFormat');
        this.filterPlanTypes();
        this.filterPurposes();
    }

    private updateScheduled = false;

    ngDoCheck() {
        if (this.isPlanHeadingAutoGenerated() && this.isPlanEditable() && !this.updateScheduled) {
            this.updateScheduled = true;

            setTimeout(() => {
                const newHeading = this.getPlanHeading();
                if (this.getModel().planHeading !== newHeading) {
                    this.getModel().planHeading = newHeading;
                }
                this.updateScheduled = false;
            }, 1000);
        }
    }

    planPurposeChange(planPurpose) {
        this.planModelService.getModel().planPurpose = planPurpose;
        const planModel = this.planModelService.getModel();

        if (
            planModel.tenureType &&
            planModel.tenureType.name &&
            planModel.planType &&
            planModel.planType.name &&
            planModel.planPurpose &&
            planModel.planPurpose.name
        ) {
            // TODO ??
            // if (planModel.hasOwnProperty('newInterestSameOwner')) {
            //     delete planModel.newInterestSameOwner;
            // }

            if (planModel.interests && planModel.interests.length) {
                planModel.interests.length = 0;
            }

            if (this.formerTenureCardinalityLookups.length) {
                this.selectedFormerTenure = this.formerTenureCardinalityLookups.filter(
                    l =>
                        l.tenureType === planModel.tenureType.name &&
                        l.planType === planModel.planType.name &&
                        l.planPurpose === planModel.planPurpose.name
                );

                // The combination of tenureType/planType/planPurpose is always unique from the
                // lookup and hence the filter is expected to return one matching record
                if (this.selectedFormerTenure && this.selectedFormerTenure.length === 1) {
                    const maxFormerTenure = this.selectedFormerTenure[0].maxFormerTenure;

                    if (maxFormerTenure === 0 && planModel.localGovernmentAuthorities.length === 0 && planModel.localities.length === 0) {
                        planModel.localities.push({localityName: '', id: undefined});
                        planModel.localGovernmentAuthorities.push({lgaName: '', id: undefined});
                    }
                }
            }
            this.eventBus.cast('nlr:plan-model-updated:planPurpose', 'planPurpose');
        }

        // Reload the former tenure details - same as if the plan number was changed
        this.planNumberChange();
        if (planModel.planPurpose) {
            this.traitsService.setPlanTraitsRequestData({planPurposeCode: planModel.planPurpose.id}).subscribe(() => {
                console.log('traits updated');
            });
        }
        // timeout(function() {
        //     $rootScope.$broadcast('resetSurveyCommentStatements');
        // }, 500);
    }

    getToolTip() {
        return this.isPlanHeadingAutoGenerated() ? 'Heading is automatically generated' : 'Enter plan heading';
    }

    tenureTypeChange(tenureType) {
        if (tenureType == undefined) return;
        this.getModel().tenureType = tenureType;
        this.getModel().planType = undefined;
        this.getModel().planPurpose = undefined;
        this.getModel().pdfCreationMethod = 'CAD_SOFTWARE';
        this.filterPlanTypes();
        this.eventBus.cast('nlr:plan-model-updated:tenureType', 'tenureType');
    }

    planTypeChange(planType) {
        if (planType === undefined) return;
        this.getModel().planType = planType;
        this.getModel().planPurpose = undefined;
        this.getModel().pdfCreationMethod = 'CAD_SOFTWARE';
        this.filterPurposes();
        this.eventBus.cast('nlr:plan-model-updated:planType', 'planType');
    }

    private filterPurposes() {
        const planType = this.planModelService.getModel().planType;
        const tenure = this.planModelService.getModel().tenureType;
        const planPurpose = this.planModelService.getModel().planPurpose;
        const pdfCreationMethod = this.planModelService.getModel().pdfCreationMethod;
        if (planType === undefined || tenure === undefined) return;
        this.planPurposes = this.planPurposeList
            .filter(row => {
                return row.active && row.planType === planType.name && row.tenureType === tenure.name;
            })
            .filter(row => {
                // Only offer purposes that support the plan's current PDF creation method. This keeps the selectable purposes
                // consistent with the method chosen via the hamburger actions, so a plan can never end up on a purpose that
                // doesn't permit its method (which would hide the matching "Create PDF" action and skew the
                // certification/design/interests applicability checks). A purpose still supporting the current method stays in
                // the list and therefore stays selected.
                return !pdfCreationMethod || (row.pdfCreationMethods?.includes(pdfCreationMethod) ?? true);
            })
            .filter(row => {
                if (this.isStageOneAmendmentOrReplacementPlan()) {
                    return row.id === planPurpose.id;
                }
                return true;
            });
    }

    private filterPlanTypes() {
        const tenureType = this.planModelService.getModel().tenureType;
        const planType = this.planModelService.getModel().planType;
        if (tenureType === undefined) return;
        const relevantPlanPurposes = this.planPurposeList
            .filter(row => row.active && row.tenureType === tenureType.name)
            .filter(row => {
                if (this.isAmendmentOrReplacementPlan() && tenureType.id === 'STRATA') {
                    return row.id === planType.id;
                }
                return true;
            });
        this.planTypes = this.planTypeList.filter(row =>
            relevantPlanPurposes.some(planPurposeRow => row.active && row.name === planPurposeRow.planType)
        );
    }
    changeStrataDetails() {
        const planPurpose = this.getModel().planPurpose;
        if (planPurpose !== undefined && planPurpose.strataRoad === 'PREPOP') {
            this.loadStrataDetails++;
        }
    }

    isPlanHeadingMandatory() {
        // The heading is mandatory whenever it is editable, i.e. whenever it is not auto-generated. This covers CAD plans as
        // well as statutory Digital/Portal plans, whose headings are entered manually rather than generated.
        return !this.isPlanHeadingAutoGenerated();
    }

    isPlanHeadingAutoGenerated() {
        // Statutory plans (FH_STAT/CR_STAT) keep a manually editable heading even though they are Digital/Portal plans,
        // whose headings are otherwise auto-generated from the plan heading format lookup.
        return ['DIGITAL_PLAN', 'SURVEYOR_PORTAL'].includes(this.getModel().pdfCreationMethod) && !this.isStatutoryPlan();
    }

    isStatutoryPlan() {
        const id = this.getModel().planPurpose?.id;
        return id === 'FH_STAT' || id === 'CR_STAT';
    }

    getModel() {
        return this.planModelService.getModel();
    }
    isPlanEditable() {
        return ['new', 'edit', 'amend', 'replace'].includes(this.action);
    }

    getPlanHeading() {
        if (!this.getModel().planPurpose || !this.isPlanHeadingAutoGenerated()) {
            return '';
        }

        const parentTenureAssociationType = this.planPurposeList.find(v => v.id === this.getModel().planPurpose.id).parentTenureAssociationType;
        const planHeadingFormatLookup = this.planHeadingFormatLookup.find(v => v.parentTenureAssociationType == parentTenureAssociationType);
        return planHeadingFormatLookup ? this.planHeadingService.getPlanHeading(planHeadingFormatLookup) : '';
    }

    planNumberChange() {
        this.changeStrataDetails();
        const model = this.planModelService.getModel();
        for (let i = 0; i < this.formerTenureValidityLookups.length; i++) {
            if (model.tenureType && model.planPurpose && model.planType) {
                if (
                    this.formerTenureValidityLookups[i].tenureType === model.tenureType.name &&
                    this.formerTenureValidityLookups[i].planType === model.planType.name &&
                    this.formerTenureValidityLookups[i].planPurpose === model.planPurpose.name
                ) {
                    if (
                        this.formerTenureValidityLookups[i].prepopFormerTenureTable != null &&
                        this.formerTenureValidityLookups[i].prepopFormerTenureTable
                    ) {
                        this.disableAddBtn = true;
                        this.applicableSubjectOfPlans = this.formerTenureSubjects.filter(fts => fts.code === 'SOCP');
                        model.formerTenureSubject = this.applicableSubjectOfPlans[0];

                        if (
                            model.planNumber &&
                            this.formerTenureValidityLookups[i].parentPlanType &&
                            this.formerTenureValidityLookups[i].parentPlanType.length > 0
                        ) {
                            const parentPlanType = this.formerTenureValidityLookups[i].parentPlanType[0];
                            this.retrieveLotsByPlanTypeAndPlanNumber(parentPlanType, model.planNumber);
                        }
                    } else {
                        model.formerTenureSubject = null;
                    }
                }
            }
        }

        // Check if the plan number is pre-populated in the former tenure
        let isPrepopulatePlanNumber = false;
        if (model.tenureType && model.tenureType.name && model.planType && model.planType.name && model.planPurpose && model.planPurpose.name) {
            if (this.formerTenureValidityLookups && this.formerTenureValidityLookups.length > 0) {
                this.selectedFormerTenureValidity = this.formerTenureValidityLookups.filter(
                    currentValidityLookup =>
                        currentValidityLookup.tenureType === model.tenureType.name &&
                        currentValidityLookup.planType === model.planType.name &&
                        currentValidityLookup.planPurpose === model.planPurpose.name
                );

                if (this.selectedFormerTenureValidity && this.selectedFormerTenureValidity.length === 1) {
                    isPrepopulatePlanNumber = this.selectedFormerTenureValidity[0].prePopParentPlan;
                }
            }
        }

        // Clear the former tenures if the plan number has changed
        if (isPrepopulatePlanNumber && this.hasPrePopulatedPlanNumberChanged()) {
            model.formerTenures = [];
            model.localities = [];
            model.localGovernmentAuthorities = [];
            model.localGovernmentAuthorities.push({lgaName: '', id: undefined});
            model.localities.push({localityName: '', id: undefined});
        }
        this.eventBus.cast('nlr:plan-model-updated:planNumber', 'planNumber');
    }

    isAmendmentOrReplacementPlan() {
        return (
            (this.getModel().lodgementType != null &&
                isDefined(this.getModel().lodgementType) &&
                this.getModel().lodgementType.value != null &&
                isDefined(this.getModel().lodgementType.value) &&
                (this.getModel().lodgementType.value.toUpperCase() === 'REPLACEMENT' ||
                    this.getModel().lodgementType.value.toUpperCase() === 'AMENDMENT')) ||
            this.readonly
        );
    }

    isStageOneAmendmentOrReplacementPlan() {
        const planModel = this.planModelService.getModel();
        return (
            this.isAmendmentOrReplacementPlan() &&
            isDefined(planModel.tenureType) &&
            planModel.tenureType.id === 'STRATA' &&
            isDefined(planModel.planStage) &&
            planModel.planStage === 1
        );
    }

    private hasPrePopulatedPlanNumberChanged() {
        let prePopulatedPlanNumberChanged = false;

        if (this.planModelService.getModel().formerTenures.length === 0) {
            return true;
        }

        for (let i = 0; i < this.planModelService.getModel().formerTenures.length; i++) {
            const formerTenure = this.planModelService.getModel().formerTenures[i];
            if (formerTenure.parentPlan !== this.planModelService.getModel().planNumber) {
                prePopulatedPlanNumberChanged = true;
            }
        }
        return prePopulatedPlanNumberChanged;
    }

    retrieveLotsByPlanTypeAndPlanNumber(parentPlanType, parentPlanNumber: number) {
        const url = this.appConfigService.settings.apiGatewayUrl + '/lots/' + parentPlanType + '/' + parentPlanNumber + '/custom';
        console.debug(this.title + ' controller retrieve lots based on parent plan number and plan type. URL: ' + url);
        this.parentLots = [];

        this.http
            .get<CustomLot[]>(url, {
                transferCache: false
            })
            .subscribe({
                next: lotList => {
                    const lots: ParentLot[] = [];
                    const oldTenures = this.getModel().formerTenures.slice();
                    if (isDefined(lotList) && lotList != null && lotList.length > 0) {
                        for (let i = 0; i < lotList.length; i++) {
                            const lot = lotList[i];
                            if (lot.lotStatus === 'RG') {
                                const register = lot.register;

                                const lotNo = parseInt(lot.lotNumber);

                                if (this.parentLots.indexOf(lotNo) === -1) {
                                    this.parentLots.push(lotNo);
                                }

                                lots.push({
                                    parentLot: lotNo,
                                    parentLotPrefix: lot.lotPrefix,
                                    parentLotType: lot.lotType,
                                    title: register,
                                    titles: lot.titles,
                                    lga: lot.lga,
                                    locality: lot.locality,
                                    lotId: lot.lotId,
                                    nonLotCommonProperty: false
                                });
                            }
                        }
                        let parentPlanTypeLookup;
                        if (isDefined(this.parentPlanTypeLookups) && this.parentPlanTypeLookups != null) {
                            for (let i = 0; i < this.parentPlanTypeLookups.length; i++) {
                                if (this.parentPlanTypeLookups[i].code === parentPlanType) {
                                    parentPlanTypeLookup = this.parentPlanTypeLookups[i];
                                }
                            }
                        }
                        if (this.hasPrePopulatedPlanNumberChanged()) {
                            this.planModelService.getModel().formerTenures = [
                                {
                                    parentPlanType: parentPlanTypeLookup,
                                    parentPlan: this.planModelService.getModel().planNumber,
                                    subjectLandDescription: '',
                                    lots: lots
                                }
                            ];
                            console.log('former tenures updated');
                        }
                    } else {
                        this.planModelService.getModel().formerTenures = [];
                    }
                    if (this.tenureService.formerTenureChanged(oldTenures, this.getModel().formerTenures)) {
                        this.planModelService.populateFormerTenureNotifications = true;
                    }
                },
                error: () => {
                    const errorMessage = 'Error retrieving lot. URL: ' + url;
                    this.alertModalService.createAlertMessage(errorMessage);
                }
            });
    }
}
